/**
 * Bitsy 3D Avatar — geometric concierge with mood-reactive animations
 * Moods: idle (default), thinking, remembering, excited
 * Returns { group, animate, setMood }
 */
import * as THREE from 'three';

const MOOD_COLORS = {
  idle:        { glow: 0xF7931A, band: 0xF7931A, light: 0xF7931A, intensity: 0.3 },
  thinking:    { glow: 0x60A5FA, band: 0x60A5FA, light: 0x60A5FA, intensity: 0.5 },
  remembering: { glow: 0xA855F7, band: 0xC084FC, light: 0xA855F7, intensity: 0.6 },
  excited:     { glow: 0xF59E0B, band: 0xF7931A, light: 0xFBBF24, intensity: 0.8 },
};

export function createBitsyAvatar(scene, position = { x: 2, y: 0, z: 1 }) {
  const group = new THREE.Group();
  group.name = 'bitsy-avatar';
  group.position.set(position.x, position.y, position.z);

  const ORANGE = 0xF7931A;
  const DARK = 0x1a1a2e;
  const WHITE = 0xffffff;

  // ─── Body ───
  const bodyMat = new THREE.MeshStandardMaterial({ color: DARK, roughness: 0.5, metalness: 0.3 });
  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.22, 0.55, 16), bodyMat);
  body.position.y = 0.55;
  body.castShadow = true;
  group.add(body);

  // ─── Accent band (mood-reactive) ───
  const bandMat = new THREE.MeshStandardMaterial({ color: ORANGE, roughness: 0.3, metalness: 0.5, emissive: ORANGE, emissiveIntensity: 0.3 });
  const band = new THREE.Mesh(new THREE.CylinderGeometry(0.185, 0.185, 0.06, 16), bandMat);
  band.position.y = 0.65;
  band.name = 'mood-band';
  group.add(band);

  // ─── Head ───
  const headMat = new THREE.MeshStandardMaterial({ color: 0xe0d5c5, roughness: 0.8 });
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.17, 16, 16), headMat);
  head.position.y = 1.0;
  head.castShadow = true;
  head.name = 'head';
  group.add(head);

  // ─── Eyes ───
  const eyeMat = new THREE.MeshStandardMaterial({ color: DARK, roughness: 0.2 });
  const eyes = [];
  [-0.06, 0.06].forEach(x => {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.03, 8, 8), eyeMat.clone());
    eye.position.set(x, 1.02, 0.14);
    eye.name = x < 0 ? 'eye-left' : 'eye-right';
    eyes.push(eye);
    group.add(eye);
  });

  // ─── Mouth ───
  const smileMat = new THREE.MeshStandardMaterial({ color: ORANGE, roughness: 0.4 });
  const smile = new THREE.Mesh(new THREE.TorusGeometry(0.04, 0.008, 8, 16, Math.PI), smileMat);
  smile.position.set(0, 0.95, 0.15);
  smile.rotation.z = Math.PI;
  smile.name = 'mouth';
  group.add(smile);

  // ─── Bellhop hat ───
  const hatMat = new THREE.MeshStandardMaterial({ color: ORANGE, roughness: 0.4, metalness: 0.3 });
  const hatBrim = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 0.02, 16), hatMat);
  hatBrim.position.y = 1.16;
  hatBrim.name = 'hat-brim';
  group.add(hatBrim);
  const hatTop = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.18, 0.1, 16), hatMat);
  hatTop.position.y = 1.22;
  hatTop.name = 'hat-top';
  group.add(hatTop);

  // ─── Hat button (mood-reactive glow) ───
  const btnMat = new THREE.MeshStandardMaterial({ color: WHITE, emissive: WHITE, emissiveIntensity: 0.4, roughness: 0.2 });
  const btn = new THREE.Mesh(new THREE.SphereGeometry(0.025, 8, 8), btnMat);
  btn.position.y = 1.28;
  btn.name = 'hat-btn';
  group.add(btn);

  // ─── Arms ───
  const armMat = new THREE.MeshStandardMaterial({ color: DARK, roughness: 0.5 });
  const arms = [];
  [-0.28, 0.28].forEach((x, i) => {
    const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.3, 8), armMat);
    arm.position.set(x, 0.6, 0);
    arm.rotation.z = i === 0 ? 0.3 : -0.3;
    arm.name = i === 0 ? 'arm-left' : 'arm-right';
    arms.push(arm);
    group.add(arm);
  });

  // ─── Base platform ───
  const baseMat = new THREE.MeshStandardMaterial({ color: ORANGE, roughness: 0.4, transparent: true, opacity: 0.6 });
  const base = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.24, 0.04, 16), baseMat);
  base.position.y = 0.02;
  base.name = 'base';
  group.add(base);

  // ─── Glow ring (mood-reactive) ───
  const glowMat = new THREE.MeshBasicMaterial({ color: ORANGE, transparent: true, opacity: 0.15, side: THREE.DoubleSide });
  const glow = new THREE.Mesh(new THREE.RingGeometry(0.26, 0.38, 32), glowMat);
  glow.rotation.x = -Math.PI / 2;
  glow.position.y = 0.01;
  glow.name = 'glow-ring';
  group.add(glow);

  // ─── Point light (mood-reactive) ───
  const bitsyLight = new THREE.PointLight(ORANGE, 0.3, 3);
  bitsyLight.position.y = 1.0;
  bitsyLight.name = 'bitsy-light';
  group.add(bitsyLight);

  // ─── Sparkle particles (for remembering/excited moods) ───
  const particleCount = 12;
  const particleGeo = new THREE.BufferGeometry();
  const positions = new Float32Array(particleCount * 3);
  for (let i = 0; i < particleCount; i++) {
    const angle = (i / particleCount) * Math.PI * 2;
    positions[i * 3] = Math.cos(angle) * 0.35;
    positions[i * 3 + 1] = 0.5 + Math.random() * 0.8;
    positions[i * 3 + 2] = Math.sin(angle) * 0.35;
  }
  particleGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const particleMat = new THREE.PointsMaterial({
    color: ORANGE, size: 0.04, transparent: true, opacity: 0,
    blending: THREE.AdditiveBlending, depthWrite: false,
  });
  const particles = new THREE.Points(particleGeo, particleMat);
  particles.name = 'mood-particles';
  group.add(particles);

  // Make meshes clickable
  group.traverse(child => {
    if (child.isMesh) child.userData = { type: 'bitsy-avatar', clickable: true };
  });

  scene.add(group);

  // ─── Mood state ───
  let currentMood = 'idle';
  let moodTransition = 0; // 0-1 blend factor

  const setMood = (mood) => {
    if (mood === currentMood) return;
    currentMood = mood;
    moodTransition = 0;

    const colors = MOOD_COLORS[mood] || MOOD_COLORS.idle;
    // Update reactive materials
    bandMat.color.setHex(colors.band);
    bandMat.emissive.setHex(colors.band);
    glowMat.color.setHex(colors.glow);
    bitsyLight.color.setHex(colors.light);
    bitsyLight.intensity = colors.intensity;
    baseMat.color.setHex(colors.glow);
    particleMat.color.setHex(colors.glow);

    // Auto-revert to idle after timeout (except idle itself)
    if (mood !== 'idle') {
      const duration = mood === 'thinking' ? 0 : 4000; // thinking stays until explicitly changed
      if (duration > 0) setTimeout(() => { if (currentMood === mood) setMood('idle'); }, duration);
    }
  };

  // ─── Animation with mood awareness ───
  const animate = (time) => {
    moodTransition = Math.min(moodTransition + 0.02, 1);
    const headMesh = group.getObjectByName('head');
    const glowRing = group.getObjectByName('glow-ring');
    const armL = group.getObjectByName('arm-left');
    const armR = group.getObjectByName('arm-right');
    const sparkles = group.getObjectByName('mood-particles');

    switch (currentMood) {
      case 'thinking': {
        // Slower bob, head tilts side to side, blue glow pulses
        group.position.y = position.y + Math.sin(time * 0.8) * 0.02;
        if (headMesh) headMesh.rotation.z = Math.sin(time * 1.5) * 0.12;
        if (glowRing) glowRing.material.opacity = 0.15 + 0.12 * Math.sin(time * 3);
        bandMat.emissiveIntensity = 0.3 + 0.3 * Math.sin(time * 3);
        // Arms closer to body (thinking pose)
        if (armL) armL.rotation.z = 0.15 + Math.sin(time * 1.2) * 0.05;
        if (armR) armR.rotation.z = -0.15 - Math.sin(time * 1.2) * 0.05;
        // Subtle particles
        if (sparkles) sparkles.material.opacity = 0.15 + 0.1 * Math.sin(time * 4);
        // Rotate particles slowly
        if (sparkles) sparkles.rotation.y = time * 0.5;
        break;
      }
      case 'remembering': {
        // Purple glow, sparkle particles orbit, dreamy float
        group.position.y = position.y + Math.sin(time * 1.0) * 0.06;
        if (headMesh) headMesh.rotation.z = Math.sin(time * 0.5) * 0.08;
        // Head tilts up slightly (looking up, remembering)
        if (headMesh) headMesh.rotation.x = -0.1;
        if (glowRing) glowRing.material.opacity = 0.2 + 0.15 * Math.sin(time * 2);
        bandMat.emissiveIntensity = 0.4 + 0.4 * Math.sin(time * 2.5);
        // Sparkle particles visible and orbiting
        if (sparkles) {
          sparkles.material.opacity = 0.4 + 0.3 * Math.sin(time * 3);
          sparkles.rotation.y = time * 1.2;
          // Float particles up
          const pos = sparkles.geometry.attributes.position;
          for (let i = 0; i < particleCount; i++) {
            pos.array[i * 3 + 1] = 0.5 + Math.sin(time * 2 + i) * 0.3 + Math.random() * 0.02;
          }
          pos.needsUpdate = true;
        }
        // Arms spread slightly (open gesture)
        if (armL) armL.rotation.z = 0.5 + Math.sin(time * 0.8) * 0.1;
        if (armR) armR.rotation.z = -0.5 - Math.sin(time * 0.8) * 0.1;
        break;
      }
      case 'excited': {
        // Bouncy animation, bright orange, fast glow, particles burst
        const bounce = Math.abs(Math.sin(time * 4)) * 0.08;
        group.position.y = position.y + bounce;
        // Quick head bob
        if (headMesh) {
          headMesh.rotation.z = Math.sin(time * 3) * 0.1;
          headMesh.rotation.x = 0;
        }
        if (glowRing) glowRing.material.opacity = 0.2 + 0.2 * Math.sin(time * 5);
        bandMat.emissiveIntensity = 0.5 + 0.5 * Math.abs(Math.sin(time * 4));
        // Arms wave excitedly
        if (armL) armL.rotation.z = 0.3 + Math.sin(time * 5) * 0.3;
        if (armR) armR.rotation.z = -0.3 - Math.sin(time * 5 + 1) * 0.3;
        // Burst particles
        if (sparkles) {
          sparkles.material.opacity = 0.5 + 0.3 * Math.sin(time * 6);
          sparkles.rotation.y = time * 2;
          const pos = sparkles.geometry.attributes.position;
          for (let i = 0; i < particleCount; i++) {
            const angle = (i / particleCount) * Math.PI * 2 + time * 2;
            const radius = 0.35 + Math.sin(time * 3 + i) * 0.1;
            pos.array[i * 3] = Math.cos(angle) * radius;
            pos.array[i * 3 + 1] = 0.5 + Math.abs(Math.sin(time * 4 + i * 0.5)) * 0.6;
            pos.array[i * 3 + 2] = Math.sin(angle) * radius;
          }
          pos.needsUpdate = true;
        }
        break;
      }
      default: { // idle
        group.position.y = position.y + Math.sin(time * 1.5) * 0.04;
        if (headMesh) {
          headMesh.rotation.z = Math.sin(time * 0.8) * 0.05;
          headMesh.rotation.x = 0;
        }
        if (glowRing) glowRing.material.opacity = 0.1 + 0.08 * Math.sin(time * 2);
        bandMat.emissiveIntensity = 0.3;
        // Default arm positions
        if (armL) armL.rotation.z = 0.3;
        if (armR) armR.rotation.z = -0.3;
        // Fade out particles
        if (sparkles) sparkles.material.opacity = Math.max(0, sparkles.material.opacity - 0.02);
        break;
      }
    }
  };

  return { group, animate, setMood };
}
